import { useEffect, useRef, useState } from "react";

import { usePanelist, usePreTask } from "../../api";
import { getApiBaseUrl } from "../../api/client";
import { getLaunchToken } from "../../api/fetchers";
import { getFlexSessionId } from "../../api/session";
import type { PretaskData } from "../../components/ui/PreTaskModal";
import type { Task } from "../../types";
import { logError } from "../../utils/logging";

export function useTaskRevealLaunch() {
  const [launchTask, setLaunchTask] = useState<Task | null>(null);
  const [isSubmittingLaunch, setIsSubmittingLaunch] = useState(false);
  const [launchError, setLaunchError] = useState<Error | null>(null);
  const launchFormRef = useRef<HTMLFormElement | null>(null);
  const autoLaunchTaskIdRef = useRef<string | null>(null);
  const panelistQuery = usePanelist();

  const preTaskQuery = usePreTask(launchTask?.uid, {
    enabled: Boolean(launchTask),
  });

  useEffect(() => {
    if (
      !launchTask ||
      preTaskQuery.isLoading ||
      preTaskQuery.isError ||
      !preTaskQuery.data
    ) {
      return;
    }

    const questions = preTaskQuery.data.questions ?? [];

    if (questions.length === 0) {
      if (autoLaunchTaskIdRef.current === launchTask.uid) {
        return;
      }

      autoLaunchTaskIdRef.current = launchTask.uid;
      void completeLaunch({ questions: {} });
      return;
    }

    autoLaunchTaskIdRef.current = null;
  }, [
    launchTask,
    preTaskQuery.data,
    preTaskQuery.isError,
    preTaskQuery.isLoading,
  ]);
  async function completeLaunch(
    preTaskAnswers: PretaskData,
    turnstileToken?: string,
  ) {
    if (!launchFormRef.current || !launchTask) {
      return;
    }
    setIsSubmittingLaunch(true);
    setLaunchError(null);
    try {
      const form = launchFormRef.current;
      const launchToken = await getLaunchToken();
      const sessionId = getFlexSessionId();

      if (!sessionId) {
        throw new Error("Your session has expired. Please sign in again.");
      }

      const launchTokenInput = form.elements.namedItem(
        "_header:x-launch-token",
      ) as HTMLInputElement | null;
      const hostnameInput = form.elements.namedItem(
        "_header:x-hostname",
      ) as HTMLInputElement | null;
      const sessionInput = form.elements.namedItem(
        "_header:x-flex-session-id",
      ) as HTMLInputElement | null;
      const preTaskInput = form.elements.namedItem(
        "preTask",
      ) as HTMLInputElement | null;
      const turnstileTokenInput = form.elements.namedItem(
        "turnstileToken",
      ) as HTMLInputElement | null;

      if (
        !launchTokenInput ||
        !sessionInput ||
        !preTaskInput ||
        !turnstileTokenInput ||
        !hostnameInput
      ) {
        throw new Error("Could not prepare launch form.");
      }

      hostnameInput.value = window.location.hostname;
      launchTokenInput.value = launchToken;
      sessionInput.value = sessionId;
      preTaskInput.value = JSON.stringify(preTaskAnswers.questions ?? {});
      turnstileTokenInput.value = turnstileToken ?? "";
      form.action = `${getApiBaseUrl()}/tasks/${launchTask.uid}/launch`;
      form.submit();
    } catch (caughtError) {
      setLaunchError(caughtError as Error);
      logError(caughtError, {
        context: "task-reveal-launch",
        taskId: launchTask.uid,
      });
      setIsSubmittingLaunch(false);
    }
  }

  function launchTaskFn(task: Task) {
    autoLaunchTaskIdRef.current = null;
    setLaunchTask(task);
  }

  function resetLaunch() {
    autoLaunchTaskIdRef.current = null;
    setLaunchTask(null);
  }

  return {
    completeLaunch,
    hasPendingPreTask: !!launchTask,
    isSubmittingLaunch,
    launchError,
    launchFormRef,
    launchTask: launchTaskFn,
    panelistQuery,
    preTaskQuery,
    preTaskQuestions: preTaskQuery.data?.questions ?? [],
    resetLaunch,
  };
}
